import { motion } from "framer-motion";
import {
  Eye,
  Printer,
  Upload,
  Mail,
  Trash2,
  ArrowRight,
  Calendar,
  User,
  Package,
  Ship,
  RotateCcw,
  FileText,
  Copy,
} from "lucide-react";
import { InlineCountryFlag } from "../shipment/CountryBadge";
const StatusBadge = require("../shipment/StatusBadge").default;

// ─── Port → country helper ────────────────────────────────────────────────────
const PORT_COUNTRY_MAP = {
  USORF: "US", USCHI: "US", USLAX: "US", USNYC: "US", USSEA: "US",
  INNSA: "IN", INMUN: "IN", INMAA: "IN", INBOM: "IN",
  CNSHA: "CN", CNNGB: "CN", CNSZX: "CN", CNTAO: "CN",
  SGSIN: "SG", AEDXB: "AE", GBFXT: "GB", NLRTM: "NL",
  DEHAM: "DE", FRLEH: "FR", JPYOK: "JP", KRPUS: "KR",
  AUSYD: "AU", CAVAN: "CA", BRSSZ: "BR", MXZLO: "MX",
};

function getCountryCodeByPort(portCode) {
  if (!portCode || typeof portCode !== "string") return null;
  const code = portCode.trim().toUpperCase();
  return PORT_COUNTRY_MAP[code] ?? (code.length >= 2 ? code.slice(0, 2) : null);
}

function getLocationName(location) {
  if (!location) return "N/A";
  return location.split(",")[0].replace(/\[.*\]/g, "").trim();
}

function formatDate(dateStr) {
  if (!dateStr) return "TBD";
  try {
    return new Date(dateStr).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

function isExpired(valideDate) {
  if (!valideDate) return false;
  return new Date() > new Date(valideDate);
}

// ─── Shipment-type pill colours ───────────────────────────────────────────────
const TYPE_PILL = {
  FCL: "bg-blue-50 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700",
  LCL: "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-700",
  RORO: "bg-amber-50 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-700",
};

// ─── Icon button ──────────────────────────────────────────────────────────────
function IconBtn({ icon: Icon, label, tone = "default", onClick }) {
  const toneCls = {
    default:
      "text-gray-500 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700",
    danger:
      "text-rose-500 hover:text-rose-700 hover:bg-rose-50 dark:text-rose-400 dark:hover:text-rose-300 dark:hover:bg-rose-900/30",
    primary:
      "text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30",
    warning:
      "text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:text-amber-300 dark:hover:bg-amber-900/30",
  }[tone];

  return (
    <button
      title={label}
      onClick={onClick}
      className={`inline-flex items-center justify-center w-8 h-8 rounded-md transition-colors ${toneCls}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}

// ─── Label / Value pair ───────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
const QuoteCard = ({
  quote,
  basePath = "rate-quote",   // "rate-quote" | "import-rate-quote" | "booking-request"
  showDeletedRecords,
  navigate,
  t,
  onOpenFileUpload,
  onOpenNotes,
  onDelete,
  onRestore,
  onCopy,
}) => {
  
  // ── Derived routes ─────────────────────────────────────────────────────────
  const id = quote?.rateQuoteSno;
  const editPath = `/${basePath}/edit/${id}`;
  const printPath = `/${basePath}/print/${btoa(id)}`;
  const emailPath = `/mail/compose?type=${basePath === "rate-quote" ? "RateQuote" : "ImportRateQuote"}&id=${id}`;

  // ── Country codes ──────────────────────────────────────────────────────────
  const originCode =
    getCountryCodeByPort(quote?.portOfLoadCode) ??
    quote?.portOfLoad?.slice(0, 2) ??
    "US";
  const destCode =
    getCountryCodeByPort(quote?.portOfDischargeCode) ??
    quote?.portOfDischarge?.slice(0, 2) ??
    "CN";

  const moveType = quote?.moveType ?? "FCL";
  const typePillCls = TYPE_PILL[moveType] ?? TYPE_PILL.FCL;
  const expired = isExpired(quote?.valideDate);
  const badgeStatus = quote?.loadingStatus === "active" ? "Active" : "Pending";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="group relative bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800
                 rounded-xl shadow-sm hover:shadow-lg hover:-translate-y-0.5
                 transition-all duration-200 ease-out overflow-hidden"
    >
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header
        className="px-5 pt-4 pb-3 flex flex-wrap items-start justify-between gap-3
                   border-b border-gray-100 dark:border-gray-800"
      >
        <div className="min-w-0">
          {/* Quote number · expiry badge */}
          <div className="flex items-center gap-2">
            <span
              className="text-[14px] font-bold text-gray-900 dark:text-gray-50 cursor-pointer
                         hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
              onClick={() => !showDeletedRecords && navigate(editPath)}
            >
              {quote?.rateQuoteNumber ?? "RQ00000000"} 🔗
              {quote?.hasAlert && (
                <span className="ml-1.5 h-2 w-2 relative inline-flex">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                </span>
              )}
            </span>

            <span
              className={`inline-flex items-center px-2 h-5 rounded-full text-[10px] font-bold ring-1 ${expired
                ? "bg-rose-50 text-rose-700 ring-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-700"
                : "bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-700"
                }`}
            >
              {expired ? "Expired" : "Active"}
            </span>
          </div>

          {/* Route row */}
          <div className="mt-1.5 flex items-center gap-2 text-[12.5px]">
            <InlineCountryFlag code={originCode} />
            <span className="font-semibold text-gray-900 dark:text-gray-100 font-mono">
              {getLocationName(quote?.portOfLoad ?? "N/A")}
            </span>
            <ArrowRight className="w-3.5 h-3.5 text-gray-400 dark:text-gray-600" />
            <InlineCountryFlag code={destCode} />
            <span className="font-semibold text-gray-900 dark:text-gray-100 font-mono">
              {getLocationName(quote?.portOfDischarge ?? "N/A")}
            </span>
          </div>
        </div>

        {/* Move-type pill + StatusBadge */}
        <div className="flex items-center gap-2 shrink-0">
          <span
            className={`inline-flex items-center px-2 h-6 rounded-md text-[10.5px] font-bold ring-1 ${typePillCls}`}
          >
            {moveType}
          </span>
          <StatusBadge status={badgeStatus} />
        </div>
      </header>

      {/* ── Body: 3-col grid ─────────────────────────────────────────────────── */}
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-3 gap-x-5 gap-y-3">
        {/* Left — Customer & Carrier */}
        <div className="space-y-3 min-w-0">
          <Field label="Customer">
            <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 truncate block">
              {quote?.customerName ?? "—"}
            </span>
            {quote?.customerEmail && (
              <div className="text-[10.5px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                {quote.customerEmail}
              </div>
            )}
          </Field>
          {(basePath === "rate-quote" || basePath === "import-rate-quote") && (
          <Field label="Carrier">
            <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
              <Ship className="w-3 h-3 text-gray-400 dark:text-gray-500 shrink-0" />
              <span className="truncate">{quote?.carrierName ?? "—"}</span>
            </span>
          </Field>
          )}
        </div>

        {/* Center — Validity dates & Container */}
        <div className="space-y-3 min-w-0">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Valid From">
              <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 inline-flex items-center gap-1">
                <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                {formatDate(quote?.effectiveDate)}
              </span>
            </Field>
            <Field label="Valid Until">
              <span
                className={`text-[12.5px] font-medium inline-flex items-center gap-1 ${expired
                  ? "text-rose-600 dark:text-rose-400"
                  : "text-gray-900 dark:text-gray-100"
                  }`}
              >
                <Calendar className="w-3 h-3 text-gray-400 dark:text-gray-500" />
                {formatDate(quote?.valideDate)}
              </span>
            </Field>
          </div>
          <Field label="Container Type">
            <span className="text-[12.5px] font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
              <Package className="w-3 h-3 text-gray-400 dark:text-gray-500 shrink-0" />
              <span className="truncate">
                {quote?.containerType ?? quote?.moveType ?? "—"}
                {quote?.containerSize && (
                  <span className="text-gray-600 dark:text-gray-400">
                    {" "}· {quote.containerSize}"
                  </span>
                )}
              </span>
            </span>
          </Field>
        </div>

        {/* Right — Quoted By & Trucker */}
        <div className="space-y-3 min-w-0">
          <Field label="Quoted By">
            <span className="text-[12.5px] font-medium inline-flex items-center gap-1.5">
              <User className="w-3 h-3 text-gray-400 dark:text-gray-500" />
              {!quote?.quotedBy ? (
                <span className="text-gray-400 dark:text-gray-500 italic">Unassigned</span>
              ) : (
                <span className="text-gray-900 dark:text-gray-100">{quote.quotedBy}</span>
              )}
            </span>
          </Field>
          <Field label="Trucker">
            <span className="text-[12.5px] font-medium text-gray-500 dark:text-gray-400">
              {quote?.truckerName ?? "N/A"}
            </span>
          </Field>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────────────── */}
      <footer
        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2
                   bg-gray-50 dark:bg-gray-800/60
                   border-t border-gray-100 dark:border-gray-800 rounded-b-xl"
      >
        {/* Left — meta info */}
        <div className="flex items-center gap-3 flex-wrap">
          <span className="flex items-center gap-1.5 text-[10.5px] text-gray-500 dark:text-gray-400">
            <User className="w-3 h-3" />
            {t("createdBy")}:{" "}
            <span className="font-medium text-gray-700 dark:text-gray-300">
              {quote?.quotedBy ?? "—"}
            </span>
          </span>
          <div className="h-3 w-px bg-gray-300 dark:bg-gray-700" />
          <span className="text-[10.5px] text-gray-500 dark:text-gray-400">
            Created: {formatDate(quote?.createdOn)}
          </span>
        </div>

        {/* Center — icon actions */}
        <div className="flex items-center gap-0.5">
          {showDeletedRecords ? (
            <IconBtn
              icon={RotateCcw}
              label={t("restore")}
              tone="warning"
              onClick={onRestore}
            />
          ) : (
            <>

              <IconBtn
                icon={Printer}
                label="Print"
                onClick={() => window.open(printPath)}
              />
              <IconBtn
                icon={Mail}
                label={t("mail")}
                tone="primary"
                onClick={() =>
                  navigate(emailPath)
                }
              />
              <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
              <IconBtn icon={Upload} label={t("upload")} onClick={onOpenFileUpload} />
              <IconBtn icon={FileText} label={t("documents")} onClick={onOpenNotes} />
              <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
              {(basePath === "rate-quote" || basePath === "import-rate-quote") && (
                <IconBtn
                  icon={Copy}
                  label="Copy Quote"
                  tone="primary"
                  onClick={() => onCopy(quote?.rateQuoteSno)}
              />
              )}
              <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
              <IconBtn
                icon={Trash2}
                label={t("delete")}
                tone="danger"
                onClick={onDelete}
              />
            </>
          )}

          {/* Right — Open button */}
          <div className="flex items-center gap-1">
            {!showDeletedRecords && (
              <button
                onClick={() => navigate(editPath)}
                className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-[11.5px] font-semibold
                           text-blue-700 bg-blue-50 hover:bg-blue-100
                           dark:text-blue-300 dark:bg-blue-900/30 dark:hover:bg-blue-900/50
                           transition-colors"
              >
                Open <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>
      </footer>
    </motion.div>
  );
};

export default QuoteCard;